import React from 'react';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import { dateToLabel } from 'further-ui/utils';
import { createEqualisationEditorRoute } from 'adminConstants/routes';
import { Edit, Delete } from '@mui/icons-material';
import TableActions from 'components/Table/TableActions';
import ErrorBoundary from 'components/ErrorBoundary';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

const pageId = 'equalisations';

const EqualisationsContent: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const [equalisationsData, query] = api.equalisations.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const mutationOpts = useHandleTrpcMutation<{
    success: boolean;
  }>();

  const deleteEqualisation = api.equalisations.delete.useMutation(
    mutationOpts({
      successMessage: 'Equalisation deleted successfully',
    }),
  );

  const equalisations = equalisationsData?.results ?? [];

  const columns: Array<Column> = [
    {
      field: 'name',
      headerName: 'Equalisation name',
      flex: 1,
      renderCell: ({ row }) => row.name,
    },
    {
      field: 'closeDate',
      headerName: 'Close date',
      flex: 1,
      renderCell: ({ row }) => dateToLabel(row.closeDate),
    },
    {
      headerName: 'Actions',
      sortable: false,
      field: 'actions',
      renderCell: ({ row }) => (
        <TableActions
          size="small"
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              color: 'primary',
              link: createEqualisationEditorRoute(row.id ?? ''),
              disabled: !row.isDraft,
            },
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              onClick: () =>
                deleteEqualisation.mutate({
                  equalisationId: row.id ?? '',
                  vehicleId: vehicle?._id ?? '',
                }),
              disabled: !row.canBeDeleted || deleteEqualisation.isPending,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <InteractiveTable
      fullHeight
      columns={columns}
      id={`${pageId}-table`}
      disableTableConfiguration
      loading={query.isFetching}
      rows={equalisations}
      rowCount={equalisations.length ?? 0}
    />
  );
};

const Equalisations: React.FC = () => {
  return (
    <PageContainer heading="Equalisations">
      <PageContent withSpacing>
        <ErrorBoundary>
          <EqualisationsContent />
        </ErrorBoundary>
      </PageContent>
    </PageContainer>
  );
};

export default Equalisations;
